import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowRight, Filter } from 'lucide-react';
import { getAllPosts } from '../data/blogData';
import SEO from '../components/SEO';

const categories = ['All', 'Industry', 'Service'];

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const posts = getAllPosts();
  const filtered = activeCategory === 'All' ? posts : posts.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-20" data-testid="blog-page">
      <div className="orange-orb orange-orb-1 top-32 -left-32 w-[28rem] h-[28rem] z-0" />
      <div className="orange-orb orange-orb-2 top-1/2 -right-32 w-[32rem] h-[32rem] z-0" />
      <div className="relative z-10">
      <SEO
        title="Blog — AI Chatbot, AI Voice Agent & AI Automation Insights"
        description="Expert insights on AI chatbots, AI voice agents, AI receptionists, AI sales agents, and workflow automation for small and mid-sized businesses. Learn how AI can transform your operations."
        keywords="AI chatbot blog, AI voice agent insights, AI receptionist guide, AI sales agent tips, AI automation for business, AI for local business blog"
        path="/blog"
      />

      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <span className="inline-block px-4 py-2 bg-white/[0.05] rounded-full text-sm font-medium text-[#A1A1AA] mb-5">
            Blog
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">
            AI Insights for Business
          </h1>
          <p className="text-lg text-[#A1A1AA] max-w-2xl">
            Practical, data-driven analysis on how AI chatbots, voice agents, and automation are transforming small and mid-sized businesses.
          </p>
        </motion.div>

        {/* Category Filter */}
        <div className="flex items-center gap-2 mb-10" data-testid="blog-filters">
          <Filter className="w-4 h-4 text-[#71717A]" />
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              data-testid={`blog-filter-${cat.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? 'bg-[#1a1a1a] text-white'
                  : 'bg-white/[0.05] text-[#A1A1AA] hover:bg-white/[0.08]'
              }`}
            >
              {cat} {cat !== 'All' && `(${posts.filter(p => cat === 'All' || p.category === cat).length})`}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        <div className="grid md:grid-cols-2 gap-6" data-testid="blog-posts-grid">
          {filtered.map((post, index) => (
            <motion.article
              key={post.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="group"
            >
              <Link to={`/blog/${post.slug}`} className="block" data-testid={`blog-card-${post.slug}`}>
                <div className="p-6 rounded-2xl border border-white/[0.08] hover:border-white/10 hover:shadow-[0_8px_32px_rgba(0,0,0,0.45)] transition-all duration-300 h-full flex flex-col">
                  {/* Category & Read Time */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      post.category === 'Industry' ? 'bg-[#FF6B00]/10 text-[#FF6B00]' : 'bg-[#FF6B00]/10 text-[#FF6B00]'
                    }`}>
                      {post.category} — {post.industry}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-[#71717A]">
                      <Clock className="w-3 h-3" /> {post.readTime}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="text-lg font-bold text-white mb-2 group-hover:text-[#A1A1AA] transition-colors leading-snug">
                    {post.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-sm text-[#A1A1AA] mb-4 flex-grow leading-relaxed">
                    {post.excerpt}
                  </p>

                  {/* Hero Stat */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-white">{post.heroStat.value}</span>
                      <span className="text-xs text-[#71717A] max-w-[180px]">{post.heroStat.label}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-white group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center p-10 bg-[#050505] rounded-2xl"
        >
          <h2 className="text-2xl font-bold text-white mb-3">Ready to see what AI can do for your business?</h2>
          <p className="text-[#A1A1AA] mb-6 max-w-lg mx-auto">Book a free 30-minute discovery call. No pitch, no pressure — just clarity on where AI fits in your business.</p>
          <a
            href="https://calendly.com/agenticatoms/30min"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="blog-cta-button"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#1a1a1a] text-white rounded-full font-medium hover:bg-white transition-colors"
          >
            Book a Free Discovery Call <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div></div>
    </div>
  );
};

export default BlogPage;
